<template>
  <div class="sound-wave-viz">
    
    <div class="viz-header" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula" v-latex><strong>公式：</strong>详见下方原理说明</div>
    </div>

    <div class="main-layout vertical">
      <!-- 混合渲染区 -->
      <div class="canvas-mixed">
        <svg width="100%" height="240" viewBox="0 0 500 240">
           <!-- 喇叭 -->
           <path d="M 20 100 L 50 100 L 70 80 L 70 160 L 50 140 L 20 140 Z" fill="#333" stroke="#4da1ff" stroke-width="2" />
           <circle cx="45" cy="120" r="10" fill="none" stroke="#4da1ff" stroke-width="1">
              <animate attributeName="r" from="10" to="15" dur="0.2s" repeatCount="indefinite" />
           </circle>

           <!-- 粒子群 (演示纵波) -->
           <g v-for="i in particleCount" :key="i">
              <circle
                :cx="getParticlePos(i).x"
                :cy="getParticlePos(i).y"
                r="1.5"
                :fill="getParticleFill(i)"
                :opacity="getParticleOpacity(i)"
              />
           </g>

           <!-- 波形辅助线 -->
           <path v-if="showWaveform" :d="sinePath" fill="none" stroke="rgba(0, 210, 255, 0.4)" stroke-width="1" />
        </svg>

        <div class="hud-mini">
           <div class="item"><span>频率 f:</span> <span>{{ frequency }} Hz</span></div>
           <div class="item"><span>波长 λ:</span> <span>{{ (340/frequency).toFixed(2) }} m</span></div>
        </div>
      </div>

      <!-- 控制面板 -->
      <div class="bottom-controls">
         <div class="control-card glass">
            <div class="row">
               <div class="col">
                  <label>声源频率 (f)</label>
                  <el-slider v-model="frequency" :min="100" :max="1000" size="mini"></el-slider>
               </div>
               <div class="col">
                  <label>振幅 (音量)</label>
                  <el-slider v-model="amplitude" :min="5" :max="30" size="mini"></el-slider>
               </div>
            </div>
            <div class="row switches">
               <el-checkbox v-model="showWaveform">显示等效横波</el-checkbox>
               <el-checkbox v-model="freeze">冻结波形</el-checkbox>
            </div>
         </div>

         <div class="lesson-card">
            <div class="point">
               <span class="dot"></span>
               <p><strong>产生：</strong>物体<strong>振动</strong>产生声音。</p>
            </div>
            <div class="point">
               <span class="dot"></span>
               <p><strong>本质：</strong>疏密相间的<strong>纵波</strong>。粒子仅在原位左右振动，不随波迁移。</p>
            </div>
         </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SoundWaveViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      frequency: 440,
      amplitude: 15,
      showWaveform: true,
      freeze: false,
      particleCount: 150,
      particles: [],
      time: 0
    }
  },
  mounted() {
    const rows = 10;
    const cols = 15;
    for(let i=0; i<rows; i++) {
        for(let j=0; j<cols; j++) {
            this.particles.push({
                originX: 80 + j * 28,
                originY: 40 + i * 18,
                phase: j * 0.4 // Spatial phase shift
            });
        }
    }
    this.animate();
  },
  computed: {
    sinePath() {
      let d = "M 80 120";
      for(let x=0; x<420; x++) {
          const t_off = x / 50; // eslint-disable-line no-unused-vars
          const y = 120 + this.amplitude * Math.sin(this.time * 10 - x * this.frequency / 500);
          d += ` L ${80 + x} ${y}`;
      }
      return d;
    }
  },
  methods: {
    getParticlePos(idx) {
      if (idx >= this.particles.length) return { x: 0, y: 0 };
      const p = this.particles[idx];
      // Longitudinal vibration: horizontal oscillation
      const offsetX = this.amplitude * Math.sin(this.time * 10 - p.originX * this.frequency / 500);
      return {
          x: p.originX + offsetX,
          y: p.originY
      };
    },
    getParticleFill(idx) {
      const p = this.getParticlePos(idx);
      const intensity = Math.abs(Math.sin(this.time * 10 - p.x * this.frequency / 500));
      return intensity > 0.8 ? '#00d2ff' : '#444';
    },
    getParticleOpacity(idx) {
       return 0.3 + 0.7 * Math.abs(Math.sin(this.time * 10 - this.getParticlePos(idx).x * this.frequency / 500));
    },
    animate() {
      if (!this.freeze) {
        this.time += 0.05;
      }
      this.handle = requestAnimationFrame(this.animate);
    }
  },
  beforeDestroy() { cancelAnimationFrame(this.handle); }
}
</script>

<style scoped lang="scss">
.sound-wave-viz {
  flex: 1; display: flex; flex-direction: column; gap: 12px; height: 100%; overflow: hidden;
}

.viz-header {
  display: flex; justify-content: space-between; align-items: center;
  .title { font-size: 1.1rem; color: #fff; font-weight: 300; }
  .status-chip { font-size: 0.65rem; color: #00d2ff; background: rgba(0,210,255,0.1); padding: 4px 10px; border-radius: 4px; border: 1px solid rgba(0,210,255,0.2); }
}

.canvas-mixed {
  flex: 1.5; background: rgba(0,0,0,0.3); border-radius: 12px; border: 1px solid rgba(255,255,255,0.05); position: relative;
  .hud-mini {
    position: absolute; bottom: 15px; left: 15px; background: rgba(0,0,0,0.6); padding: 8px 12px; border-radius: 6px; border: 1px solid rgba(255,255,255,0.1);
    .item { font-size: 0.7rem; color: #fff; display: flex; justify-content: space-between; gap: 10px; span:first-child { color: #888; } }
  }
}

.bottom-controls {
  flex: 0.5; display: flex; flex-direction: column; gap: 10px; overflow-y: auto; padding-bottom: 5px;
}

.control-card {
  background: rgba(255, 255, 255, 0.03); border-radius: 12px; padding: 12px 18px;
  .row { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 8px; &:last-child { margin-bottom: 0; } }
  .col { label { display: block; font-size: 0.7rem; color: #aaa; margin-bottom: 5px; } }
  .switches { display: flex; align-items: center; gap: 20px; }
}

::v-deep .el-checkbox__label { color: #888; font-size: 0.7rem; }

.lesson-card {
   background: rgba(0, 210, 255, 0.03); border-radius: 12px; padding: 12px 15px; display: flex; flex-direction: column; gap: 8px;
   .point { display: flex; gap: 10px; align-items: flex-start; .dot { width: 6px; height: 6px; background: #00d2ff; border-radius: 50%; margin-top: 5px; flex-shrink: 0; } p { margin: 0; font-size: 0.75rem; color: #888; line-height: 1.4; strong { color: #fff; } } }
}
</style>
